import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Facebook, Twitter, Instagram, Youtube, Mail, ArrowRight, CheckCircle2, MapPin, Phone, MessageCircle } from 'lucide-react';
import Pennant from '../common/Pennant';
import AdBanner from '../common/AdBanner';
import { subscribeApi } from '../../utils/api';
import { useSiteSettings } from '../../hooks/useSiteSettings';

// फुटरको डिफल्ट रङ = मिनी नेभबारको रङ (bg-green-400)
export const FOOTER_DEFAULT_BG = '#4ade80';

const CATEGORIES = [
  { id: 'politics', label: 'राजनीति' },
  { id: 'samaj', label: 'समाज' },
  { id: 'sports', label: 'खेलकुद' },
  { id: 'entertainment', label: 'मनोरञ्जन' },
  { id: 'world', label: 'विश्व' },
  { id: 'tech', label: 'प्रविधि' },
  { id: 'opinion', label: 'विचार' },
];

function Footer({ onCategoryClick }) {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [msg, setMsg] = useState('');
  // Shared settings hook — admin le update garepachhi live refresh hunchha
  const settings = useSiteSettings() || {};

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      await subscribeApi.subscribe(email.trim());
      setDone(true);
      setMsg('सदस्यता लिनुभएकोमा धन्यवाद!');
      toast.success('सदस्यता लिनुभएकोमा धन्यवाद!');
      setEmail('');
    } catch (err) {
      const m = err.response?.data?.error || 'सदस्यता लिन असफल भयो।';
      setMsg(m);
      setDone(false);
      toast.error(m);
    }
  };

  const SOCIAL_DEFAULTS = {
    facebook: 'https://www.facebook.com',
    twitter: 'https://twitter.com',
    instagram: 'https://www.instagram.com',
    youtube: 'https://www.youtube.com',
  };

  const socialHref = (key) => settings[key] || SOCIAL_DEFAULTS[key] || '#';

  // WhatsApp — admin le number/link rakhnu parchha, narakhe dekhaindaina
  const waRaw = (settings.whatsapp || '').trim();
  const waLink = waRaw
    ? waRaw.startsWith('http')
      ? waRaw
      : `https://wa.me/${waRaw.replace(/[^0-9]/g, '')}`
    : '';

  const socials = [
    { key: 'facebook', Icon: Facebook, label: 'Facebook', href: socialHref('facebook') },
    { key: 'twitter', Icon: Twitter, label: 'Twitter', href: socialHref('twitter') },
    { key: 'instagram', Icon: Instagram, label: 'Instagram', href: socialHref('instagram') },
    { key: 'youtube', Icon: Youtube, label: 'Youtube', href: socialHref('youtube') },
    ...(waLink ? [{ key: 'whatsapp', Icon: MessageCircle, label: 'WhatsApp', href: waLink }] : []),
  ];

  // Admin-configurable appearance; default = mini nav bg color
  const bgColor = settings.bg_color || FOOTER_DEFAULT_BG;

  return (
    <>
      {/* Snake/zigzag decorative edge rising out of the footer */}
      <svg className="block w-full h-[14px]" preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <pattern id="footer-snake" width="26" height="14" patternUnits="userSpaceOnUse">
            <path d="M0 14 L13 0 L26 14 Z" fill={bgColor} />
          </pattern>
        </defs>
        <rect width="100%" height="14" fill="url(#footer-snake)" />
      </svg>

      <footer className="text-ink-soft" style={{ backgroundColor: bgColor }}>
        {/* Ad strip */}
        <div className="max-w-[1240px] mx-auto px-5 pt-6 sm:pt-8">
          <AdBanner position="footer" />
        </div>

        {/* Main grid — 4 aligned columns */}
        <div className="max-w-[1240px] mx-auto px-5 pt-8 pb-4 sm:pt-10 sm:pb-6">
          <div className="grid grid-cols-2 md:grid-cols-12 gap-6 lg:gap-10">

            {/* Brand column — श्रेणीहरू tira ali right sarni modern spacing */}
            <div className="col-span-2 md:col-span-4 min-w-0 flex flex-col items-start md:pl-8 xl:pl-14">
              {settings.footer_logo ? (
                <img src={settings.footer_logo} alt="Kalo Jhola" className="h-20 sm:h-24 md:h-28 w-auto mb-3 object-contain drop-shadow-sm" />
              ) : (
                <span className="font-display text-[26px] sm:text-[30px] font-bold flex items-center gap-2 mb-3">
                  <Pennant size={28} /> KALO<em className="not-italic text-crimson">JHOLA</em>
                </span>
              )}
              <p className="text-[14px] leading-relaxed max-w-[300px] text-ink">
                {settings.about_text || 'निष्पक्ष, विश्वसनीय र समयमै — नेपालको मुटुबाट।'}
              </p>
              {(settings.editor_name || settings.darta_number || settings.press_council || settings.company_reg_number) && (
                <div className="text-[12.5px] sm:text-[13px] font-semibold text-ink mt-3 flex flex-col gap-0.5">
                  {settings.editor_name && (
                    <span>
                      सम्पादक: <span className="text-ink">{settings.editor_name}</span>
                    </span>
                  )}
                  {settings.darta_number && <span>दर्ता प्रमाणपत्र नं.: {settings.darta_number}</span>}
                  {settings.press_council && <span>प्रेस काउन्सिल नं.: {settings.press_council}</span>}
                  {settings.company_reg_number && <span>कम्पनी रजिस्टरको कार्यालय.: {settings.company_reg_number}</span>}
                </div>
              )}
              <div className="flex gap-2 mt-4">
                {socials.map(({ key, Icon, label, href }, i) => (
                  <a
                    key={i}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    title={label}
                    className="w-9 h-9 rounded-full bg-white/85 border border-white text-ink flex items-center justify-center shadow-sm hover:bg-crimson hover:border-crimson hover:text-white hover:-translate-y-0.5 transition-all"
                  >
                    <Icon size={15} />
                  </a>
                ))}
              </div>
            </div>

            {/* Categories — responsive wrap chips, left aligned */}
            <div className="col-span-2 md:col-span-3 min-w-0">
              <h5 className="font-display text-white text-[15px] font-bold tracking-[0.04em] uppercase pb-2.5 mb-3.5 border-b border-white/40">श्रेणीहरू</h5>
              <div className="flex flex-wrap gap-2">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.id}
                    className="group/chip inline-flex items-center gap-1.5 rounded-full bg-white/85 border border-white/80 pl-2.5 pr-3 py-1.5 text-[12.5px] font-semibold text-ink shadow-sm transition-all duration-200 hover:bg-crimson hover:border-crimson hover:text-white hover:-translate-y-0.5 hover:shadow-md active:translate-y-0"
                    onClick={() => onCategoryClick(c.id)}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-crimson/70 transition-colors duration-200 group-hover/chip:bg-white" />
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Quick links — modern pill buttons */}
            <div className="col-span-1 md:col-span-2 min-w-0">
              <h5 className="font-display text-white text-[15px] font-bold tracking-[0.04em] uppercase pb-2.5 mb-3.5 border-b border-white/40">द्रुत लिंक</h5>
              <div className="flex flex-col gap-2">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-white/85 border border-white/80 rounded-full px-3.5 py-2 text-[13px] font-semibold text-ink shadow-sm transition-all duration-200 hover:bg-crimson hover:border-crimson hover:text-white hover:-translate-y-0.5 w-fit"
                >
                  <Phone size={13} /> सम्पर्क
                </Link>
                <Link
                  to="/hamro-tim"
                  className="inline-flex items-center gap-2 bg-white/85 border border-white/80 rounded-full px-3.5 py-2 text-[13px] font-semibold text-ink shadow-sm transition-all duration-200 hover:bg-crimson hover:border-crimson hover:text-white hover:-translate-y-0.5 w-fit"
                >
                  <MapPin size={13} /> हाम्रो टिम
                </Link>
              </div>
            </div>

            {/* Newsletter column */}
            <div className="col-span-2 md:col-span-3 min-w-0">
              <h5 className="font-display text-white text-[15px] font-bold tracking-[0.04em] uppercase pb-2.5 mb-3.5 border-b border-white/40">सदस्यता</h5>
              <p className="text-[13.5px] text-ink mb-3">ताजा समाचारका लागि सदस्यता लिनुहोस्।</p>
              <form
                className="flex items-center gap-2 bg-white/90 border border-white rounded-full pl-4 pr-1.5 py-1.5 text-ink shadow-sm focus-within:border-crimson transition-colors"
                onSubmit={subscribe}
              >
                <Mail size={15} className="flex-shrink-0" />
                <input
                  type="email"
                  placeholder="इमेल ठेगाना"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-transparent outline-none flex-1 text-[13px] text-ink min-w-0"
                />
                <button
                  aria-label="Subscribe"
                  type="submit"
                  className="flex-shrink-0 w-9 h-9 rounded-full bg-crimson text-white flex items-center justify-center hover:bg-crimson-dark transition-colors"
                >
                  <ArrowRight size={16} />
                </button>
              </form>
              {msg && (
                <p className={`mt-2 text-[12px] font-semibold flex items-center gap-1 ${done ? 'text-green-700' : 'text-crimson'}`}>
                  {done && <CheckCircle2 size={14} />} {msg}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Bottom bar — copyright + mini socials (right) + credits */}
        <div className="border-t border-white/50 bg-black/5">
          <div className="max-w-[1240px] mx-auto px-5 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-[13px] font-semibold text-ink order-2 sm:order-1">
              {settings.copyright || '© २०८३ Kalo Jhola News। सर्वाधिकार सुरक्षित।'}
            </p>

            {/* Right side — credits */}
            <div className="order-1 sm:order-2 flex items-center">
              <a
                href="https://www.zeroinfinitytechnologies.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[13px] font-bold text-violet-700 hover:text-crimson transition-colors"
                aria-label="Zero Infinity Technology"
              >
                Powered by <span className="font-display">Zero Infinity Technology</span>
              </a>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}

export default Footer;